import { jsx } from 'react/jsx-runtime';
import { createContext, forwardRef, useMemo, useRef, useEffect, useId, useLayoutEffect } from 'react';
import { useInteractionHandler, SKIP_RECORDING } from '../hooks/use-interaction-handler.js';
import { useMergeRefs } from '../hooks/use-merge-refs.js';
import { useSharedRef } from '../hooks/use-shared-ref.js';
import { useValidatedContext } from '../hooks/use-validated-context.js';
import '../popover-primitive/popover-primitive.arrow.js';
import { Container } from '../popover-primitive/popover-primitive.container.js';
import { usePopover } from '../popover-primitive/popover-primitive.hooks.js';
import { ScrollContainer } from '../scroll-container/scroll-container.js';
import { preventDefault } from '../utils/event.js';
import { getListboxId, getOptionId, getPopupId } from './autocomplete-primitive.utils.js';

const AutocompletePrimitiveListContext = createContext(null);
/**
 * A list of AutocompletePrimitive.Options.
 * @see https://proto.figma.design/primitives/autocomplete
 */
function PopupList({ anchorEl, offset: offsetProp = 8, placement = 'bottom-start', ...props }) {
    const popupId = getPopupId(props.id);
    const { getContainerProps, getTriggerProps } = usePopover({
        isOpen: props.expanded,
        offset: offsetProp,
        onOpenChange: props.onExpand,
        placement,
        provideOwnClick: true, // this prevents the automatic dismissal when clicking in the popup
        nodeId: popupId,
        softDismiss: true,
        type: 'tooltip',
    });
    // on expand, re-focus on input
    useLayoutEffect(() => {
        if (props.expanded && props.refs?.input.current) {
            props.refs.input.current.focus();
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs are not memoizable
    [props.expanded]);
    // update Floating UI positioning
    useLayoutEffect(() => {
        if (anchorEl?.current) {
            getTriggerProps().ref(anchorEl.current);
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want to ever run this on expanded, regardless of other values
    [props.expanded]);
    return (jsx(Container, { ...getContainerProps(), onPointerDown: preventDefault, children: jsx(List, { ...props }) }));
}
PopupList.displayName = 'AutocompletePrimitive.PopupList';
const _AutocompletePrimitiveList = forwardRef(({ activeValue, children, className, expanded, id, inputMode, onExpand, onSelect, refs, registerOption, selected, setActiveValue, setInputMode, style, ...props }, ref) => {
    const contextValue = useMemo(() => ({
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- ignore any type here
        activeValue,
        expanded,
        id,
        inputMode,
        setActiveValue,
        setInputMode,
        onExpand,
        onSelect,
        refs,
        selected,
        registerOption,
    }), 
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs not memoizable
    [
        activeValue,
        expanded,
        id,
        inputMode,
        setActiveValue,
        setInputMode,
        onExpand,
        onSelect,
        selected,
        registerOption,
    ]);
    return (jsx(AutocompletePrimitiveListContext.Provider, { value: contextValue, children: jsx(ScrollContainer, { ...props, role: "listbox", id: getListboxId(id), styles: { root: { className, style } }, ref: useMergeRefs(ref, refs.scroll), onPointerDown: (evt) => {
                evt.preventDefault();
                props.onPointerDown?.(evt);
            }, children: children }) }));
});
_AutocompletePrimitiveList.displayName = 'AutocompletePrimitive.List';
/**
 * A list of AutocompletePrimitive.Options.
 * @see https://proto.figma.design/primitives/autocomplete
 */
const List = _AutocompletePrimitiveList;
const _AutocompletePrimitiveOption = forwardRef(({ value, children, disabled, id, recordingKey, onSelect: onSelectIn, ...props }, refIn) => {
    const ref = useSharedRef(refIn);
    const { 
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- this is any "any" type only here, but not in consumers
    activeValue, inputMode, id: uuid, refs, selected, onSelect: onSelectRoot, setActiveValue, setInputMode, registerOption, } = useValidatedContext(AutocompletePrimitiveListContext, 'AutocompletePrimitive.Option', 'AutocompletePrimitive.List');
    const onClick = useInteractionHandler((event) => {
        event.preventDefault();
        if (disabled) {
            props.onClick?.(event);
            event.stopPropagation(); // prevent clicking from closing expanded container
            return SKIP_RECORDING;
        }
        onSelectRoot?.(value, { event });
        onSelectIn?.(value);
        refs.input.current?.focus(); // re-focus on input, if available
        props.onClick?.(event);
    }, { eventName: 'click', recordingKey }, [onSelectRoot, onSelectIn, disabled, value]);
    const isOptionSelected = selected?.includes(value);
    const onSelectCustomRef = useRef(onSelectIn);
    useEffect(() => registerOption(ref.current, {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- this is being overly cautions from our “any” internal typing
        value,
        onSelect: onSelectCustomRef.current,
    }), 
    // eslint-disable-next-line  react-hooks/exhaustive-deps -- ref and registerOption aren’t true deps
    [value]);
    const generatedId = useId();
    let optionId = id;
    if (!optionId) {
        optionId = typeof value === 'string' ? getOptionId({ uuid, localId: value }) : generatedId;
    }
    return (jsx("div", { "data-fpl-component": "primitive", ...props, id: optionId, ref: ref, role: "option", "aria-selected": isOptionSelected, "aria-disabled": disabled, onClick: onClick, onPointerMove: (evt) => {
            if (!disabled && inputMode === 'keyboard') {
                setInputMode('pointer');
            }
            props?.onPointerMove?.(evt);
        }, onPointerEnter: (evt) => {
            if (!disabled && inputMode === 'pointer' && activeValue !== value) {
                setActiveValue(value); // this is an expensive callback, so only act on meaningful changes
                refs.input.current?.focus(); // only refocus on significant changes
            }
            props?.onPointerEnter?.(evt);
        }, "data-activedescendant": activeValue === value || undefined, children: children }));
});
_AutocompletePrimitiveOption.displayName = 'AutocompletePrimitive.Option';
/**
 * Option to be rendered inside AutocompletePrimitive.List
 * @see https://fpl.figma.design/primitives/autocomplete
 */
const Option = _AutocompletePrimitiveOption;
/**
 * Optional grouping for AutocompletePrimitive.Options.
 * @see https://fpl.figma.design/primitives/autocomplete
 */
const Group = forwardRef((props, ref) => (jsx("div", { "data-fpl-component": "primitive", ...props, role: "group", ref: ref })));
Group.displayName = 'AutocompletePrimitive.Group';
/**
 * Manually add a separator between options in a select dropdown. `AutocompletePrimitive.Group` is the preferred
 * way of separating options, as groups automatically have a visual separation from surrounding
 * options and provide a semantic label for the group. Only use a manual `AutocompletePrimitive.Separator` when
 * groups semantically don't make sense.
 */
function Separator({ className }) {
    return jsx("li", { "data-fpl-component": "primitive", className: className, role: "separator" });
}
Separator.displayName = 'AutocompletePrimitive.Separator';

export { Group, List, Option, PopupList, Separator };
//# sourceMappingURL=autocomplete-primitive.list.js.map
